<template>
    <div ref="rootRef" :style="rootStyle">
        <div class="v-sticky" :class="{
            'v-sticky--fixed': state.fixed,
        }" :style="stickyStyle">
            <slot />
        </div>
    </div>
</template>

<script>
import { defineComponent, ref, reactive, computed, watch, onMounted } from "@nuxtjs/composition-api";
import { useElementBounding, useEventListener, useIntersectionObserver } from "@vueuse/core";
import {useScrollParent} from "~/composables/useScrollParent";
import { isHidden, getScrollTop } from '~/utils/dom'
export default defineComponent({
    name: 'VSticky',
    props: {
        position: {
            type: String,
            default: 'top'
        },
        offsetTop: {
            type: Number,
            default: 0
        },
        offsetBottom: {
            type: Number,
            default: 0
        }
    },
    emits: ['change'],
    setup(props, { emit }) {
        const rootRef = ref();
        const scrollParent = useScrollParent(rootRef);
        const state = reactive({
            fixed: false,
            width: 0, // root width
            height: 0, // root height
            transform: 0,
        });

        const offset = computed(() => props.position === 'top' ? props.offsetTop : props.offsetBottom);
        const rootStyle = computed(() => {
            const { fixed, height, width } = state;
            if (fixed) {
                return {
                    width: `${width}px`,
                    height: `${height}px`,
                };
            }
        })

        const stickyStyle = computed(() => {
            if (!state.fixed) {
                return;
            }
            const style = {
                width: `${state.width}px`,
                height: `${state.height}px`,
                [props.position]: `${offset.value}px`,
            }
            if (state.transform) {
                style.transform = `translate3d(0, ${state.transform}px, 0)`;
            }

            return style
        })

        const emitScroll = (scrollTop) => {
            emit('scroll', {
                scrollTop,
                isFixed: state.fixed,
            });
        }

        const rootRect = reactive(useElementBounding(rootRef));
        const onScroll = () => {
            if (!rootRef.value || isHidden(rootRef)) {
                return;
            }

            const { container, position } = props;
            // const _rootRect = useRect(root);
            const scrollTop = getScrollTop(window);

            state.width = rootRect.width;
            state.height = rootRect.height;


            if (position === 'top') {
                // The sticky component should be kept inside the container element
                if (container) {
                    const containerRect = container.getBoundingClientRect();
                    const difference = containerRect.bottom - offset.value - state.height;
                    state.fixed = offset.value > rootRect.top && containerRect.bottom > 0;
                    state.transform = difference < 0 ? difference : 0;
                } else {
                    state.fixed = offset.value > rootRect.top;
                }
            } else {
                const { clientHeight } = document.documentElement;
                if (container) {
                    const containerRect = container.getBoundingClientRect(container);
                    const difference =
                        clientHeight - containerRect.top - offset.value - state.height;
                    state.fixed =
                        clientHeight - offset.value < rootRect.bottom &&
                        clientHeight > containerRect.top;
                    state.transform = difference < 0 ? -difference : 0;
                } else {
                    state.fixed = clientHeight - offset.value < rootRect.bottom;
                }
            }
            emitScroll(scrollTop);
        }

        watch(
            () => state.fixed,
            (value) => emit('change', value)
        );


        useEventListener(scrollParent, 'scroll', onScroll, {
            passive: true
        })

        onMounted(() => {
            onScroll();
        })
        return {
            rootRef,
            rootStyle,
            state,
            stickyStyle
        }
    },
    mounted() {
        console.log(this.$attrs);
    }
})
</script>

<style lang="scss">

:root {
    --v-sticky-z-index: 10;
}

.v-sticky {
    &--fixed {
        position: fixed;
        z-index: var(--v-sticky-z-index);
    }
}

</style>